import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Container } from 'components/base';

import PdpIcon from '../product/pdpIcon';
import ProductFaq from '../product/productFaq';

// Sections shared by the public and the signed-in /refer-friend (docs/refer-friend.md).
// Styles: styles/_refer-friend.scss (rf- prefix).

export const TERMS_HREF = 'https://knowledge-base--parabo.groovehq.com/help/refer-a-friend';

export const HERO_FACTS = [
  { icon: 'users', label: 'Up to 50 friends' },
  { icon: 'wallet', label: 'Up to $500 in credit' },
  { icon: 'calendarCheck', label: 'Credit valid for 1 year' },
];

const STEPS = [
  {
    icon: 'send',
    title: 'Share your code',
    text: 'Send it by text, email or a group chat. It’s six characters, short enough to read out loud.',
  },
  {
    icon: 'tag',
    title: 'Your friend saves $10',
    text: 'They enter your code at checkout on their first order, in the app or on the web. $10 comes off their prints and display items.',
  },
  {
    icon: 'gift',
    title: 'You earn $10 in credit',
    text: 'Once their order ships, $10 in Parabo credit lands in your account. Use it on your next order.',
  },
];

// Shape ProductFaq renders. The pages add their own last question.
export const FAQ_COMMON = [
  {
    caption: 'Who can use my code?',
    description:
      'Anyone placing their first Parabo Press order. Codes don’t work for returning customers.',
  },
  {
    caption: 'How many times can my code be used?',
    description: '50 times. That’s up to $500 in Parabo credit for you.',
  },
  {
    caption: 'When does my credit arrive?',
    description: 'After your friend’s order ships. You’ll see it in Parabo Credit in your account.',
  },
  {
    caption: 'What does my friend’s $10 off cover?',
    description: 'The products in their first order. It doesn’t apply to shipping.',
  },
  {
    caption: 'What does my credit cover?',
    description: 'Earned Parabo Credit can cover your order, including shipping.',
  },
  {
    caption: 'How long is my credit valid?',
    description: 'Earned credit is valid for 1 year.',
  },
];

export const HeroFacts = ({ className }) => (
  <ul className={classNames('rf-hero-facts', className)}>
    {HERO_FACTS.map(({ icon, label }) => (
      <li key={label}>
        <PdpIcon name={icon} size={16} />
        {label}
      </li>
    ))}
  </ul>
);

HeroFacts.propTypes = {
  className: PropTypes.string,
};

export const HowItWorks = ({ className }) => (
  <section className={classNames('rf-section', className)}>
    <Container>
      <p className="rf-eyebrow">How it works</p>
      <h2 className="rf-h2 rf-steps-title">Three steps. The middle one is theirs.</h2>
      <ol className="rf-steps">
        {STEPS.map((step, index) => (
          <li key={step.title} className="rf-step">
            <div className="rf-step-top">
              <span className="rf-step-num">{index + 1}</span>
              <span className="rf-step-badge">
                <PdpIcon name={step.icon} size={22} />
              </span>
            </div>
            <div>
              <h3 className="rf-step-title">{step.title}</h3>
              <p className="rf-step-text">{step.text}</p>
            </div>
          </li>
        ))}
      </ol>
    </Container>
  </section>
);

HowItWorks.propTypes = {
  className: PropTypes.string,
};

export const Faq = ({ items, onTermsClick }) => (
  <section className="rf-section rf-faq">
    <Container>
      <ProductFaq
        eyebrow="The fine print"
        title="Questions about referrals"
        intro={
          <>
            Something else? Our help center has the full Refer a Friend terms.
            <a
              href={TERMS_HREF}
              className="rf-terms-link"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onTermsClick}
            >
              Read the full terms
            </a>
          </>
        }
        items={items}
      />
    </Container>
  </section>
);

Faq.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({ caption: PropTypes.string, description: PropTypes.string }),
  ).isRequired,
  onTermsClick: PropTypes.func,
};

// One dot per friend, ten per row. Decorative, the numbers next to it carry the meaning.
export const Dots = ({ total, filled, className }) => (
  <ul className={classNames('rf-dots', className)} aria-hidden="true">
    {Array.from({ length: total }, (_, index) => (
      <li key={index} className={classNames('rf-dot', { 'rf-dot--earned': index < filled })} />
    ))}
  </ul>
);

Dots.propTypes = {
  total: PropTypes.number.isRequired,
  filled: PropTypes.number.isRequired,
  className: PropTypes.string,
};
